import { defaultPageViewMap } from '@common/Tracking';
import {
    DL_SHOW_BREADCRUMB_PATH,
    DL_SHOW_NAV_ID,
    DL_SHOW_PATH_ID,
    DL_SHOW_TOP_NAV
} from '@common/Tracking/constants/GaDataLayerRules';
import { GA_SCREEN_PRODUCT } from '@common/Tracking/constants/GaScreenNames';
import { get as getCookie } from '@helpers/cookie';
import { getCustomerDataFromCookie, getCustomerId, getVisitorDataCookieAsObject } from '@helpers/visitor';

/**
 * Format data keys names
 *
 * @param {object} data
 * @return {object}
 */
export const formatData = (data, map = defaultPageViewMap) => {
    const formattedData = {
        login_status: 'visitor'
    };

    Object.entries(data).forEach(([key, value]) => {
        if (map.has(key)) {
            let newValue = value || null;

            if (key === 'CI' && value === '0') {
                newValue = null;
            }

            if (key === 'CG' && value === 'U') {
                newValue = 'UNKNOWN';
            }

            formattedData[map.get(key)] = newValue;
        }
    });

    return formattedData;
};

/**
 * Get id from GA cookie
 *
 * @return {number}
 */
export const getGaClientId = () => {
    const gaCookie = decodeURIComponent(getCookie('_ga'));
    return Math.floor(parseFloat(gaCookie.replace(/GA1\.[0-9]\./, '') || 0));
};

/**
 * Generate attributes on banners that are necessary for tracking impressions and clicks
 *
 * @return {array}
 */
export const generateTrackingAttributes = () => {
    const {
        controller = '',
        action = ''
    } = document.body.dataset;
    const parentElementSelectors = [];

    if (controller === 'layout' && action === 'renderbyconfig') {
        parentElementSelectors.push('cms-container');
    } else if (controller === 'layout' && action === 'templatebynavid') {
        parentElementSelectors.push('universal-frontpage');
    } else if (controller === 'listing' && action === 'list') {
        parentElementSelectors.push('p-card--listing-banner');
        const powerBrandWall = document.querySelector('.power-brandwall');
        const brandWall = document.querySelector('.banner-block-power-brandwall');
        const cmsBlockItems = document.querySelectorAll('.cms-block-item');
        let selectorBrandWall = null;

        if (powerBrandWall) {
            parentElementSelectors.push('power-brandwall');
            selectorBrandWall = powerBrandWall.querySelectorAll('.power-brandwall__column > a');
        } else if (brandWall) {
            parentElementSelectors.push('banner-block-power-brandwall');
            selectorBrandWall = brandWall.querySelectorAll('.banner-block-power-brandwall__banner-image-link');
        }

        cmsBlockItems.forEach((item, itemIteration) => {
            parentElementSelectors.push(`cms-block-container--${itemIteration}`);
        });

        if (selectorBrandWall !== null) {
            selectorBrandWall.forEach((element, iteration) => {
                const { page_path: pagePath = '' } = App.Config?.GaTrackingPage || {};

                if (pagePath) {
                    const { navGender } = App.Config;
                    const parts = pagePath.split('/');

                    if (typeof parts[3] !== 'undefined') {
                        let gaBannerId = element.dataset.bannerId ?? 0;
                        gaBannerId = gaBannerId > 0 ? `~${gaBannerId}` : '';
                        element.dataset.trackingId = `brandwall.power_ban${gaBannerId}~${parts[3]}~`;
                        element.dataset.trackingName = `${navGender}/brandwall`;
                        element.dataset.trackingPosition = `power_ban_${iteration}`;
                        const img = element.querySelector('img');

                        if (img) {
                            element.dataset.trackingCreative = img
                                .src
                                .split('/')
                                .pop()
                                .split('.')
                                .shift();
                        }
                    }
                }
            });
        }
    } else if (controller === 'checkout' && action === 'index') {
        parentElementSelectors.push('checkout-banner');
    }

    return parentElementSelectors;
};

export const assembleGaPageViewData = (pageViewMap, customAttributes = {}) => {
    const {
        topNavigation,
        page_path: pagePath,
        page_type: pageType,
        breadcrumb_path: breadcrumbPath
    } = App.Config.GaTrackingPage;

    const currentPageType = customAttributes.pageType ?? pageType;
    const eventData = {
        gaEventData: {
            ...formatData(getVisitorDataCookieAsObject(), pageViewMap),
            ...App.Config.GaTrackingData,
            page_path: customAttributes.pagePath ?? pagePath,
            page_type: currentPageType,
            top_navigation: null,
            client_id: getGaClientId(),
            customer_id: getCustomerId().toString(),
            common_contact_id: getCustomerDataFromCookie('CCI', 0),
            oauth_provider: getCustomerDataFromCookie('COP', null)
        },
        snowplowEventData: {
            nav_id: 0,
            path_id: 0,
            page_type: currentPageType
        }
    };

    if (typeof eventData.gaEventData.user_id !== 'undefined') {
        delete eventData.gaEventData.user_id;
    }

    if (typeof eventData.gaEventData.nav_id !== 'undefined') {
        delete eventData.gaEventData.nav_id;
    }

    if (customAttributes.pageTitle) {
        eventData.gaEventData.page_title = customAttributes.pageTitle;
    }

    if (DL_SHOW_NAV_ID.includes(currentPageType)) {
        eventData.gaEventData.nav_id = App.Config.GaTrackingPage?.nav_id || 0;
        eventData.snowplowEventData.nav_id = App.Config.GaTrackingPage?.nav_id || 0;
    }

    if (DL_SHOW_PATH_ID.includes(currentPageType)) {
        eventData.gaEventData.path_id = App.Config.GaTrackingPage?.pathId || 0;
        eventData.snowplowEventData.path_id = App.Config.GaTrackingPage?.pathId || 0;
    }

    if (DL_SHOW_TOP_NAV.includes(currentPageType)) {
        eventData.gaEventData.top_navigation = topNavigation || '';
    }

    if (DL_SHOW_BREADCRUMB_PATH.includes(currentPageType)) {
        eventData.gaEventData.breadcrumb_path = breadcrumbPath;
    }

    if (currentPageType === GA_SCREEN_PRODUCT) {
        eventData.gaEventData.page_path = App.Config?.Product?.trackingPage || eventData.gaEventData.page_path;
        eventData.gaEventData.top_navigation = App.Config?.Product?.trackingTopNavigation || eventData.gaEventData.top_navigation;

        eventData.snowplowEventData = {
            page_type: currentPageType,
            item_id: App.Config?.Product?.currentProductId || '',
            item_variant: App.Config?.Product?.currentVariantId || ''
        };

        eventData.snowplowEventData.item_id = eventData.snowplowEventData.item_id.toString();
        eventData.snowplowEventData.item_variant = eventData.snowplowEventData.item_variant.toString();
    }

    return eventData;
};

const getListGroupData = (
    group,
    hasToAddPathName,
    hasToAddPathId,
    hasToAddNavigationId,
    hasToAddTagIds
) => {
    const listGroupData = ['l', group];

    if (hasToAddPathName) {
        listGroupData.push(App?.Config?.GaTrackingPage?.page_path ?? window.location.pathname);
    }

    if (hasToAddPathId) {
        listGroupData.push(hasToAddPathId);
    }

    if (hasToAddNavigationId) {
        listGroupData.push(hasToAddNavigationId);
    }

    if (hasToAddTagIds) {
        listGroupData.push(hasToAddTagIds);
    }

    return listGroupData;
};

export const formatListGroupData = (data, separator = ':') => data.join(separator);

export const getFormatListGroupData = (
    {
        listGroupDataExtraIds = [],
        column,
        currentPage,
        group,
        isFromCMS = false,
        position,
        productsPerRow,
        productsPerPage,
        row,
        totalProducts,
        tiAddPath = ''
    }
) => [
    formatListGroupData(
        getListGroupData(
            group,
            isFromCMS ? true : tiAddPath,
            ...listGroupDataExtraIds
        )
    ),
    formatListGroupData([
        'c',
        column,
        productsPerRow
    ]),
    formatListGroupData([
        'p',
        position,
        totalProducts
    ]),
    formatListGroupData([
        'r',
        Math.floor(row + ((productsPerPage * (currentPage - 1)) / productsPerRow)),
        Math.ceil(totalProducts / productsPerRow)
    ]),
    formatListGroupData([
        'pa',
        currentPage,
        Math.ceil(totalProducts / productsPerPage)
    ])
];
